import { useEffect, useMemo, useState } from "react";
import { validatePassword } from "firebase/auth";
import { auth } from "../utils/firebase";
import {
  FALLBACK_RULES,
  formRulesFromRules,
  rulesFromStrengthOptions,
} from "../constants/passwordPolicy";

// `validatePassword` fetches the project's policy and reports it on the result,
// so any password serves as the probe — only `passwordPolicy` is read. A
// non-empty one avoids depending on how the SDK treats an empty string.
const PROBE = "probe";

/**
 * The password rules this Firebase project actually enforces.
 *
 * Fetched once per mount rather than per keystroke: the rules are then
 * evaluated locally, which keeps the checklist synchronous while the user
 * types. Falls back to the bundled constants when the policy cannot be read or
 * carries no rules, so the form never renders an empty checklist and never
 * accepts a password on the strength of having asked for nothing.
 *
 * Reading the policy is what stops the displayed rules drifting from the
 * enforced ones, which is a mismatch users meet as a rejection with no
 * explanation.
 */
export const usePasswordPolicy = () => {
  const [strengthOptions, setStrengthOptions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    validatePassword(auth, PROBE)
      .then((status) => {
        if (!cancelled) {
          setStrengthOptions(status?.passwordPolicy?.customStrengthOptions);
        }
      })
      .catch(() => {
        // Leaving the state null keeps the fallback in place. A password screen
        // that cannot reach Firebase has bigger problems than its checklist,
        // and the server rejects anything non-compliant regardless.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rules = useMemo(() => {
    const live = rulesFromStrengthOptions(strengthOptions);
    return live.length ? live : FALLBACK_RULES;
  }, [strengthOptions]);

  const formRules = useMemo(() => formRulesFromRules(rules), [rules]);

  return { rules, formRules };
};
